import { Box, Button, ButtonGroup, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Input, List, ListDivider, ListItem, ListItemDecorator, Modal, ModalDialog, ModalOverflow, Stack, Switch, Tooltip, Typography } from '@mui/joy';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { useDashboard } from '@providers/DashboardProvider';
import { useCreateUserMeta, useDeleteUserMeta, useGetAllUserMeta, useUpdateUserMeta } from '@api/userMetaService';
import { useEffect, useState } from 'react';
import pluralize from 'pluralize';
import { DeleteRowButton } from '@components/objects/DeleteRowButton';
import { useNavigate, useParams } from 'react-router-dom';
import { authenticatedApi } from 'api';
import toast from "react-hot-toast";
import { queryClient } from "@api/queryClient";
import { events } from '@utils/analytics';

export const ManageDashboards = (props) => {
    let { id = 0 } = useParams();
    const {isEditing, setIsEditing, isConfiguringWidget} = useDashboard();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dashboards, setDashboards] = useState([]);

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = useGetAllUserMeta();

    useEffect(() => {
      if(!data?.data) {
        setDashboards([]);
        return;
      }

      const _dashboards = data.data
          .filter(meta => meta.meta_key.includes('dashboard_settings'))
          .map(meta => {
              if(typeof meta.meta_value === 'string') {
                  meta.meta_value = JSON.parse(meta.meta_value);
              }

              return {
                id: meta.id,
                dashboardKey: parseInt(meta.meta_key.replaceAll(/[^0-9]/g, '')),
                primary: !!meta.meta_value.isPrimaryDashboard,
                name: meta.meta_value.name,
                widgets: meta.meta_value.widgets.length,
                to_be_deleted: false,
                meta_value: meta.meta_value,
              }
          });

        setDashboards(_dashboards);
    }, [data]);

    const setRowValue = (rowIndex, key, value) => {
        let _dashboards = [...dashboards];
        _dashboards[rowIndex][key] = value;
        setDashboards(_dashboards);
    };

    const addRow = () => {
        const maxKey = dashboards.reduce(function(prev, current) {
            return (prev && prev.dashboardKey > current.dashboardKey) ? prev : current
        }).dashboardKey;

        let newRow = {
            id: null,
            dashboardKey: maxKey+1,
            primary: false,
            name: '',
            widgets: 0,
            to_be_deleted: false,
            is_unsaved: true,
        };

        let _dashboards = [...dashboards, newRow];
        setDashboards(_dashboards);

        setTimeout(() => {
            const row = document.querySelector(`#dashboard-ind-${_dashboards.length-1}`);
            row?.scrollIntoView({ behavior: "smooth", /*block: "end", inline: "nearest"*/ });
        }, 10);
    }

    const markRowForDeletion = (rowIndex, mark = true) => {
        const dashboard = dashboards[rowIndex];

        // if it's empty, just delete it
        if(dashboard.is_unsaved && !dashboard.name) {
            let _dashboards = [...dashboards];
            _dashboards.splice(rowIndex, 1);
            setDashboards(_dashboards);
        } else {
            setRowValue(rowIndex, 'to_be_deleted', mark);
        }
    }

    const onSubmit = () => {
        setSaving(true);
        setOpen(false);

        let createCount = 0;
        let deleteCount = 0;

        let promises = [];
        // create new dashboards
        const newDashboards = dashboards.filter(dashboard => dashboard.id === null && !dashboard.to_be_deleted);
        for(const newDashboard of newDashboards) {
            let action = authenticatedApi.post('/user-meta', {
                meta_key: `dashboard_settings[${newDashboard.dashboardKey}]`,
                meta_value: {
                    name: newDashboard.name,
                    widgets: [],
                }
            });
            promises.push(action);
            createCount++;
        }

        // delete dashboards
        const deletedDashboards = dashboards.filter(dashboard => dashboard.id !== null && dashboard.to_be_deleted);
        const currentDashboardDeleted = deletedDashboards.find(dashboard => parseInt(dashboard.dashboardKey) === parseInt(id));
        for(const deletedDashboard of deletedDashboards) {
            let action = authenticatedApi.delete(`/user-meta/${deletedDashboard.id}`);
            promises.push(action);
            deleteCount++;
        }

        // update dashboards
        const updatedDashboards = dashboards.filter(dashboard => dashboard.id !== null && !dashboard.to_be_deleted);
        for(const updatedDashboard of updatedDashboards) {
            let newValue = {
                meta_value: {
                    ...updatedDashboard.meta_value,
                    name: updatedDashboard.name,
                },
            };
            let action = authenticatedApi.put(`/user-meta/${updatedDashboard.id}`, newValue);
            promises.push(action);
        }

        const performUpdate = Promise.all(promises);

        toast.promise(performUpdate, {
            loading: 'Updating...',
            success: `The dashboards were updated.`,
            error: 'There was an error while updating.',
        });

        performUpdate.finally(() => {
            // invalidate the current
            setSaving(false);
            setOpen(false);

            if(createCount > 0) {
                events.objectCreated({type: 'dashboard'});
            }

            if(deleteCount > 0) {
                events.objectDeleted({type: 'dashboard'});
            }

            // navigate to main dashboard if we deleted the one we were just on
            if(currentDashboardDeleted) {
                navigate(`/dashboard`, { replace: true });
            }

            queryClient.invalidateQueries({ queryKey: ['user-meta'] })
        });
    }

    return (
        <>
            <Tooltip title="Edit Dashboards">
              <IconButton
                variant='outlined'
                size='sm'
                onClick={()=>setOpen(true)}
                loading={saving}
                sx={{
                  transition: 'var(--mm-default-transition)',
                  ...(!isEditing ? {
                    opacity: 0,
                    pointerEvents: 'none',
                  } : {}),
                }}
                {...props}
              >
                <EditNoteRoundedIcon/>
              </IconButton>
            </Tooltip>

            <Modal open={open} /*onClose={() => setOpen(false)}*/>
            <ModalOverflow>
                <ModalDialog
                    sx={{
                        width: '100%',
                        maxWidth: '40rem',
                    }}
                >
                    <DialogTitle
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        Manage Dashboards
                        <Tooltip title={'Add Dashboard'}>
                            <IconButton
                                size="sm"
                                color="neutral"
                                onClick={addRow}
                                sx={{
                                bgcolor: 'background.level1',
                                '--variant-outlinedHoverBg': '#FFFFFF6B',
                                '--variant-outlinedActiveBg': '#FFFFFFB8',// theme.palette.background.level1
                                '--IconButton-size': '1.65rem',
                                'svg': {
                                    width: '1.05rem',
                                    height: '1.05rem',
                                }
                                }}
                            >
                                <AddRoundedIcon/>
                            </IconButton>
                            </Tooltip>
                    </DialogTitle>
                    <Divider/>
                    <List size="sm">
                    {dashboards.map((dashboard, dashboardIndex) => {
                        const isNew = !dashboard.is_unsaved;
                        {/* const formData = data[rowIndex] ?? null; */}
                        const isDeleted = dashboard?.to_be_deleted ?? false;

                        return (
                            <ListItem
                                key={dashboardIndex}
                                id={`dashboard-ind-${dashboardIndex}`}
                                // className={`${isDeleted && 'to-delete'}`}
                                sx={{
                                    borderRadius: 'sm',
                                    boxShadow: 'sm',
                                    border: 1,
                                    borderColor: 'background.level3',
                                    // mx: 1,
                                    my: 0.5,
                                    p: 1.5,
                                    opacity: dashboard.primary ? 0.7 : 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{flex: 1}}>
                                    {dashboard.primary && (
                                        <Typography fontSize={'md'}>{dashboard.name}</Typography>
                                    )}
                                    {!dashboard.primary && (
                                        <Input
                                            placeholder='e.g. Organization'
                                            name={`${dashboardIndex}-name`}
                                            disabled={isDeleted}
                                            value={dashboard.name} // this isn't working for some reason
                                            onChange={(e) => {
                                                setRowValue(dashboardIndex, 'name', e.target.value);
                                            }}
                                            // endDecorator={dashboard.widgets ? <WarningRoundedIcon/> : null}
                                            sx={{
                                                maxWidth: '300px',
                                            }}
                                        />
                                    )}
                                    <Typography
                                        fontSize={'sm'}
                                        fontStyle={'italic'}
                                        sx={theme => ({
                                            mt: 0.5,
                                            ...(isDeleted && {
                                                color: theme.palette.danger[500],
                                                // fontWeight: 'bold',
                                            }),
                                        })}
                                    >
                                        {!!dashboard.is_unsaved && (<>New dashboard</>)}
                                        {!dashboard.is_unsaved && (<>
                                        {dashboard.widgets} {pluralize('widget', dashboard.widgets)}
                                            {isDeleted && (
                                            <Typography fontWeight={'bold'}>
                                            {' '}will be permanently deleted
                                            </Typography>
                                            )}
                                        </>)}
                                    </Typography>
                                </Box>
                                <ListItemDecorator
                                    sx={{
                                        marginInlineEnd: 0,
                                    }}
                                >
                                    {dashboard.primary && (
                                        <Typography fontSize={'sm'} fontStyle={'italic'}>Main Dashboard</Typography>
                                    )}
                                    {!dashboard.primary && (
                                        <DeleteRowButton data={dashboards} index={dashboardIndex} toggle={markRowForDeletion}/>
                                    )}
                                </ListItemDecorator>
                            </ListItem>
                        )
                    })}
                    </List>

                    <DialogActions>
                        <Button
                            disabled={saving}
                            type="button" // this can't be submit type or it will submit the parent form
                            onClick={() => {
                                onSubmit();
                            }}
                        >
                            Save
                        </Button>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => {
                                setOpen(false);
                                try {
                                    // onCancel();
                                } catch(e) {
                                    //
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </ModalOverflow>
            </Modal>
        </>
    )
};
